import type { Mixpanel } from "mixpanel-browser";

import getAppConfig, { env, isLocal } from "@liberty-fund/config";
import { Logger } from "@liberty-fund/logger";

/**
 * Central Mixpanel wrapper for the portal.
 *
 * Structure mirrors the Concierge clients — the typed `EventMap` comes from
 * concierge-app/src/infrastructure/analytics.ts, the lazy-load + queue from
 * concierge-web/src/analytics.js — but the portal writes to its **own**
 * Mixpanel project per environment (`mixpanel.token` in
 * packages/config/config.json), so its events never mix into Concierge
 * funnels. Event names still follow the same "Object Action" Title Case
 * convention, and `distinct_id` uses the same email-then-sub precedence, so a
 * person can be lined up across projects if that's ever wanted.
 *
 * To add an event: add its name + property shape to `EventMap`, then call
 * `analytics.track()` at the call site. TypeScript enforces the shape.
 *
 * Privacy: never send free text a user typed (chat messages, notes, search
 * terms, referral details). Send lengths, counts, ids and outcomes instead.
 */

// ---------------------------------------------------------------------------
// Event map — every tracked event and its property shape
// ---------------------------------------------------------------------------

/** Which portal sub-app a page or action belongs to. */
export type PortalApp =
  | "dashboard"
  | "liberty-ai"
  | "board"
  | "honorarium"
  | "people"
  | "tax-forms"
  | "debug"
  | "unknown";

/** Outcome of an action that talks to the API. */
type Status = "success" | "error";

export type EventMap = {
  // Session / auth. `User Signed In` fires once per page load that resolves
  // an authenticated Auth0 session (matching concierge-web), not only on an
  // interactive login.
  "User Signed In": Record<string, never>;
  "Login Started": {
    trigger: "auto" | "retry" | "reauth" | "session_expired";
  };
  "Login Failed": { error_code?: string; error_message?: string };
  "Access Denied Viewed": Record<string, never>;
  "Sign Out Prompted": Record<string, never>;
  "Sign Out Cancelled": Record<string, never>;
  "User Signed Out": Record<string, never>;

  // Navigation. `Page Viewed` is fired automatically on every route change by
  // AnalyticsTracker; the click events record *how* the user got there.
  "Page Viewed": {
    page: string;
    app: PortalApp;
    path_template: string;
    previous_page?: string;
    conference_id?: string;
    conferee_id?: string;
    person_id?: string;
    form_name?: string;
  };
  "Navigation Clicked": {
    item: string;
    destination: string;
    location: "sidebar" | "breadcrumb" | "dashboard" | "not_found";
  };
  "Mobile Menu Opened": Record<string, never>;
  "Tab Selected": { app: PortalApp; page: string; tab: string };
  "Print Clicked": { page: string };

  // Global search palette (Cmd/Alt+K)
  "Search Opened": { trigger: "click" | "keyboard" };
  "Search Performed": {
    query_length: number;
    conference_results: number;
    people_results: number;
  };
  "Search Result Selected": {
    result_type: "conference" | "person";
    result_id: string;
  };

  // Board
  "Conference Watchlist Toggled": {
    conference_id: string;
    action: "added" | "removed";
    source: "list" | "details";
    status: Status;
  };
  "Observation Request Opened": {
    conference_id: string;
    source: "list" | "details";
  };
  "Observation Request Submitted": {
    conference_id: string;
    status: Status;
  };
  "Observations Approval Opened": {
    selected_count: number;
    scope: "conference" | "all";
  };
  "Observations Approved": {
    selected_count: number;
    failed_count: number;
    scope: "conference" | "all";
    status: Status;
  };
  "Document Collection Opened": { collection: string };
  "Conference Document Opened": {
    conference_id: string;
    document: "proposal" | "readings";
    source: "details" | "readings_table";
    status: Status;
  };
  "Conference Hotel Phone Clicked": { conference_id: string };
  "Conference Hotel Website Clicked": { conference_id: string };

  // Honorarium
  "Honorarium Payment Status Changed": {
    conference_id: string;
    value: string;
    scope: "row" | "all";
  };
  "Honorarium Conferee Notes Opened": { conference_id: string };
  "Honorarium Conferee Notes Saved": { conference_id: string; status: Status };
  "Honorarium Payment Review Opened": {
    conference_id: string;
    can_submit: boolean;
    approved_count: number;
    missing_count: number;
    missing_notes_count: number;
  };
  "Honorarium Payments Submitted": {
    conference_id: string;
    approved_count: number;
    status: Status;
  };

  // Tax forms
  "Tax Filter Changed": { filter: string; value: string };
  "Tax Conferee Flag Toggled": {
    conference_id: string;
    flagged: boolean;
    status: Status;
  };
  "Tax Conferee Details Opened": { conference_id: string };
  "Tax Conferee Details Saved": { conference_id: string; status: Status };
  "Tax Form Opened": {
    form_name: string;
    source: "details" | "direct_link";
    status: Status;
  };
  "Tax Form Upload Started": { conference_id: string };
  "Tax Form Uploaded": {
    conference_id: string;
    form_format: string;
    status: Status;
  };
  "Tax Form Deleted": { form_format: string; status: Status };

  // People
  "Referral Submitted": {
    status: Status | "validation_error";
    has_email?: boolean;
  };

  // LibertyAI
  "LibertyAI Message Sent": {
    new_conversation: boolean;
    message_length: number;
    status: Status;
  };
  "LibertyAI Conversation Selected": Record<string, never>;
  "LibertyAI New Conversation Started": Record<string, never>;
  "LibertyAI Conversation Renamed": { status: Status };
  "LibertyAI Conversation Deleted": { was_active: boolean; status: Status };
  "LibertyAI Conversation Marked Unread": Record<string, never>;
  "LibertyAI Message Copied": Record<string, never>;
  "LibertyAI Message Forked": Record<string, never>;
  "LibertyAI Load Retried": { scope: "conversations" | "messages" };
};

export type EventName = keyof EventMap;

/** Options for events fired immediately before the page unloads. */
interface TrackOptions {
  /**
   * Deliver with `navigator.sendBeacon`, which the browser completes even as
   * the page goes away. Use for anything followed by a full-page redirect
   * (sign-in, sign-out); an ordinary XHR is cancelled by the unload.
   */
  beforeUnload?: boolean;
}

// ---------------------------------------------------------------------------
// Instance — loaded lazily, with calls queued until it's ready
// ---------------------------------------------------------------------------

// mixpanel-browser is large, and nothing about rendering the app depends on
// it, so it's dynamically imported rather than sitting in the main bundle.
// Calls made before it resolves are queued and replayed in order.
let mixpanelInstance: Mixpanel | null = null;
let ready = false;
let initStarted = false;
const queue: Array<(mixpanel: Mixpanel) => void> = [];

function callWhenReady(fn: (mixpanel: Mixpanel) => void): void {
  if (ready && mixpanelInstance) {
    fn(mixpanelInstance);
  } else if (initStarted) {
    queue.push(fn);
  }
  // else: init() was never called, or this environment has no real token —
  // drop silently rather than queue forever.
}

// ---------------------------------------------------------------------------
// Before-unload events fired while the SDK is still loading
// ---------------------------------------------------------------------------

// Sign-in and sign-out redirect straight after tracking. If that happens
// before the lazy import resolves, the in-memory queue dies with the page, so
// those events are also written here and replayed by the next page load's
// init() if the old page never got to send them. `time`, `$insert_id` and
// `distinct_id` are fixed when the event is recorded, so a replay lands at
// the right moment, against the right person, and Mixpanel discards it as a
// duplicate if the beacon did go out.
//
// A sign-out's reset() is lost the same way, and without it the next page
// would carry on as the signed-out user. It goes in the same list, in order,
// so a replay resets the identity exactly between the events before the
// sign-out and the ones after it, even across several pages that each
// unloaded before their SDK loaded.
const PENDING_KEY = "liberty-fund-portal:mixpanel-pending";
const MAX_PENDING = 20;

type PendingEntry =
  | {
      kind: "event";
      id: string;
      event: string;
      properties: Record<string, unknown>;
    }
  | { kind: "reset"; id: string };

/** The id last passed to identify(), known before the SDK has loaded. */
let identifiedId: string | undefined;

function readPending(): PendingEntry[] {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(PENDING_KEY) ?? "[]",
    );
    return Array.isArray(parsed) ? (parsed as PendingEntry[]) : [];
  } catch {
    return [];
  }
}

function writePending(entries: PendingEntry[]): void {
  try {
    if (entries.length) {
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify(entries.slice(-MAX_PENDING)),
      );
    } else {
      localStorage.removeItem(PENDING_KEY);
    }
  } catch {
    // Storage unavailable (private mode, quota): the in-memory queue still
    // covers every case except an unload before the SDK loads.
  }
}

function addPending(entry: PendingEntry): void {
  writePending([...readPending(), entry]);
}

function removePending(id: string): void {
  writePending(readPending().filter((p) => p.id !== id));
}

function newInsertId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The SDK sends the full URL as `$current_url`. Hashes and query strings here
 * can carry personal data (tax-forms links put a conferee's name in the
 * hash), so only origin + path is ever sent.
 */
function currentUrl(): string {
  return window.location.origin + window.location.pathname;
}

function stripQueryAndHash(url: unknown): unknown {
  if (typeof url !== "string" || !url) return url;
  try {
    const { origin, pathname } = new URL(url);
    return origin + pathname;
  } catch {
    // Not a URL: the SDK's "$direct" referrer marker, or nothing worth keeping.
    return url === "$direct" ? url : undefined;
  }
}

/**
 * Runs on every event the SDK sends, including ones it generates itself
 * (`$identify` on a change of identity), which never pass through track()
 * below. A replayed event keeps the path it was recorded on.
 */
function sanitizeUrls(
  event: string,
  properties: Record<string, unknown>,
): [string, Record<string, unknown>] {
  return [
    event,
    {
      ...properties,
      $current_url: stripQueryAndHash(properties.$current_url),
      $referrer: stripQueryAndHash(properties.$referrer),
      $initial_referrer: stripQueryAndHash(properties.$initial_referrer),
    },
  ];
}

/**
 * Profile updates don't pass through `on_track`. The SDK's own
 * `people.set_once({ $initial_referrer, ... })` at init is the one that
 * carries a URL, so the profile operations get the same treatment before
 * they are sent.
 */
function sanitizeProfileUrls(
  update: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized = { ...update };
  for (const op of ["$set", "$set_once"] as const) {
    const props = sanitized[op];
    if (props && typeof props === "object" && "$initial_referrer" in props) {
      sanitized[op] = {
        ...props,
        $initial_referrer: stripQueryAndHash(
          (props as Record<string, unknown>).$initial_referrer,
        ),
      };
    }
  }
  return sanitized;
}

/**
 * Super properties ride on every event. `platform` distinguishes the portal
 * the same way the Concierge clients use 'web' / 'mobile'.
 */
function registerDefaults(mixpanel: Mixpanel): void {
  mixpanel.register({ platform: "portal", environment: env });
}

/** reset() also clears super properties, so the defaults go straight back. */
function resetIdentity(mixpanel: Mixpanel): void {
  mixpanel.reset();
  registerDefaults(mixpanel);
}

/** True for a token that has actually been provisioned. */
export function isConfiguredToken(token: string | undefined): boolean {
  return !!token && !token.startsWith("PLACEHOLDER");
}

/**
 * Starts Mixpanel for this environment. Safe to call more than once; a no-op
 * when the token is empty or still a PLACEHOLDER. Like RUM, analytics must
 * never be on the critical path, so every failure leaves the app running.
 */
function init(): void {
  if (initStarted) return;

  const token = getAppConfig().mixpanel?.token;
  if (!isConfiguredToken(token)) {
    Logger.info(
      `Mixpanel disabled: no token configured for "${env}" in packages/config/config.json`,
    );
    return;
  }

  initStarted = true;
  // Read before anything this page tracks can add to it: whatever is there
  // now was left behind by a page that unloaded first.
  const leftovers = readPending();
  import("mixpanel-browser")
    .then(({ default: mixpanel }) => {
      mixpanel.init(token, {
        autocapture: false,
        track_pageview: false,
        persistence: "localStorage",
        debug: isLocal,
        // Marketing enrichment copies raw URL parameters (utm_term and the
        // like, which can hold a name) into separate properties the URL
        // sanitizers below never see. The portal has no campaigns to
        // attribute, so all of it is off: per-event UTM and click ids,
        // first-touch initial_utm_* on profiles, persisted UTM super
        // properties, and the search-engine keyword super property.
        // A real option (read in track()), missing from the SDK's types.
        ...({ track_marketing: false } as object),
        skip_first_touch_marketing: true,
        store_google: false,
        stop_utm_persistence: true,
        property_blacklist: ["mp_keyword"],
        hooks: {
          on_track: sanitizeUrls,
          // Run for every profile request ("before_send_" + "people"), but
          // missing from the SDK's type definitions.
          ...({ before_send_people: sanitizeProfileUrls } as object),
        },
      });
      registerDefaults(mixpanel);
      mixpanelInstance = mixpanel;
      ready = true;
      // Leftovers first, in the order they happened, and all before anything
      // in this page's queue. Events are sent immediately: they come from
      // redirects, and this page may be about to redirect again (straight on
      // to Auth0, say).
      leftovers.forEach((entry) => {
        if (entry.kind === "reset") {
          resetIdentity(mixpanel);
        } else {
          mixpanel.track(entry.event, entry.properties, BEACON);
        }
        removePending(entry.id);
      });
      queue.splice(0).forEach((fn) => fn(mixpanel));
    })
    .catch((error: unknown) => {
      // The import failed (offline, blocked by an extension) or init threw on
      // a blocked storage backend. Drop the queue so it can't grow forever,
      // and allow a later init() to retry.
      Logger.warn("Mixpanel failed to initialise; analytics disabled", {
        message: (error as Error)?.message,
      });
      initStarted = false;
      queue.length = 0;
    });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// The SDK batches events by default, and its enqueue step is asynchronous;
// a redirect can unload before a batched event is stored or sent, whatever
// the transport. These go straight out.
const BEACON = { transport: "sendBeacon", send_immediately: true } as const;

function track<E extends EventName>(
  event: E,
  // No second arg for no-property events; required otherwise. A trailing
  // options object is allowed in both cases.
  ...args: EventMap[E] extends Record<string, never>
    ? [properties?: undefined, options?: TrackOptions]
    : [properties: EventMap[E], options?: TrackOptions]
): void {
  const [properties, options] = args;
  if (isLocal) {
    Logger.debug(`[Mixpanel] ${event}`, properties ?? {});
  }
  const props = { ...properties, $current_url: currentUrl() };

  if (!options?.beforeUnload) {
    callWhenReady((mixpanel) => mixpanel.track(event, props));
    return;
  }

  if (ready || !initStarted) {
    callWhenReady((mixpanel) => mixpanel.track(event, props, BEACON));
    return;
  }

  // SDK still loading, and the page is about to go: persist it as well.
  const id = newInsertId();
  const pending = {
    kind: "event" as const,
    id,
    event,
    properties: {
      ...props,
      time: Date.now() / 1000,
      $insert_id: id,
      ...(identifiedId && { distinct_id: identifiedId }),
    },
  };
  addPending(pending);
  callWhenReady((mixpanel) => {
    mixpanel.track(event, pending.properties, BEACON);
    removePending(id);
  });
}

interface UserTraits {
  $email?: string;
  $name?: string;
  $picture?: string;
  /** Portal sub-apps the user's roles grant, e.g. ["board", "tax-forms"]. */
  portal_apps?: string[];
  roles?: string[];
}

function identify(userId: string | undefined, traits?: UserTraits): void {
  if (!userId) return;
  identifiedId = userId;
  callWhenReady((mixpanel) => {
    mixpanel.identify(userId);
    if (traits) mixpanel.people.set(traits);
  });
}

/** Super properties attached to every subsequent event. */
function register(properties: Record<string, unknown>): void {
  callWhenReady((mixpanel) => mixpanel.register(properties));
}

/** Clears the identity so the next user on this browser starts fresh. */
function reset(): void {
  identifiedId = undefined;
  if (ready) {
    callWhenReady(resetIdentity);
    return;
  }
  // Sign-out redirects right after this. Unless the SDK is up, the call is
  // queued (dies with the page) or dropped (init failed), so record it, in
  // sequence with any pending events, for the next load too.
  const id = newInsertId();
  addPending({ kind: "reset", id });
  callWhenReady((mixpanel) => {
    resetIdentity(mixpanel);
    removePending(id);
  });
}

export const analytics = { init, track, identify, register, reset };
